import React, { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  opacityVariants,
  springVariants,
  delayedOpacityVariants,
  homeViewport,
  springViewport
} from "../utils/framerVariants";

gsap.registerPlugin(ScrollTrigger);

const Stages = ({ data }) => {

  const isVideo = data.Media && data.Media.mime.startsWith("video");
  const isFullscreen = data.Layout && data.Layout.startsWith("FullScreen");
  const isBlack = data.Headlinecolor && data.Headlinecolor.startsWith("black");

  if (!data.Media) {
    return null; // Render nothing if data.Media is null
  }

  const controls = useAnimation();
  const svgRef = useRef(null);

  useEffect(() => {
  const revealAnimation = () => {
    controls.start({ pathLength: 1 });
  };

  const scrollTrigger = ScrollTrigger.create({
    trigger: svgRef.current,
    start: "top center",
    onEnter: revealAnimation,
  });

  return () => {
    scrollTrigger.kill(); // Cleanup ScrollTrigger instance on unmount
  };
  }, [controls]);

  const isRichTextCaption = data.Media.caption && data.captionText && data.captionText.trim() === "";

  return (
    <article className="container mx-auto max-w-none mb-16 md:mb-24 lg:mb-32 xl:mb-48 relative">
      <motion.div
        variants={delayedOpacityVariants}
        initial="invisible"
        whileInView="visible"
        viewport={springViewport}
        className="stage-screen"
      >
        {isFullscreen ? (
          <div className="relative fullScreen">
            <div className="md:h-screen w-full overflow-hidden">
              {!isVideo &&
                <GatsbyImage
                  image={getImage(data.Media.file)}
                  alt={data.Media.alternativeText || "Alternative Text missing"}
                  className="h-full w-full object-cover"
                />
              }
              {isVideo &&
                <video className="h-full w-full object-cover" playsInline autoPlay loop muted>
                  <source src={data.Media.file.url} type="video/mp4" />
                </video>
              }
              {/*{data.Text && (
                  <div className="absolute top-0 left-0 w-full md:h-screen z-[10]">
                    <div className="flex items-center justify-center text-center h-screen">
                      <div className="basis-full mx-auto max-w-6xl lg:max-w-7xl 2xl:max-w-8xl px-24 lg:px-32 xl:px-32 2xl:px-8 3xl:px-24 4xl:px-8">
                        {data.Subheadline && (
                          <motion.p
                            variants={springVariants}
                            initial="invisible"
                            whileInView="visible"
                            viewport={springViewport}
                            className={`mb-0 text-text tracking-text leading-text text-center font-semibold max-w-5xl mx-auto ${
                              isBlack ? "text-black" : "text-white"
                            }`}
                          >
                            {data.Subheadline}
                          </motion.p>
                        )}
                        <motion.div
                          variants={springVariants}
                          initial="invisible"
                          whileInView="visible"
                          viewport={springViewport}
                          className={`richtextstage ${isBlack ? "text-black" : "text-white"}`}
                          dangerouslySetInnerHTML={{
                            __html: data.Text,
                          }}
                        />
                      </div>
                    </div>
                  </div>
            )}*/}
            </div>
            {isRichTextCaption ? (
              <>
                {data.Media.caption && (
                  <p className="md:absolute inline-flex px-4 sm:px-4 mt-2 mb-0 pr-12 md:pr-24 lg:pr-32 text-text-sm tracking-text-sm leading-text-sm text-black/50 max-w-4xl">
                    {data.Media.caption}
                  </p>
                )}
              </>
            ) : (
              <div className="caption-rich-text-stage" dangerouslySetInnerHTML={{ __html: data.captionText }} />
            )}
          </div>
        ) : (
          <div>
            <div className="h-[50vh] md:h-[60vh] lg:h-[80vh] w-full relative overflow-hidden">
              {!isVideo &&
                <GatsbyImage
                  image={getImage(data.Media.file)}
                  alt={data.Media.alternativeText || "Alternative Text missing"}
                  className="h-full w-full object-cover"
                />
              }
              {isVideo &&
                <video className="h-full w-full object-cover" playsInline autoPlay loop muted>
                  <source src={data.Media.file.url} type="video/mp4" />
                </video>
              }
              {data.Text && (
                <div className="absolute z-[2] top-[-40vh] left-[80vw]">
                  <div className="rotate-[-10deg] relative top-[-50px] md:top-[-80px] lg:top-[-80px] xl:top-[-100px]2 xl:top-[-120px] 2xl:top-[-140px] left-[-0px] lg:left-[-40px] xl:left-[-140px] ">
                    <svg
                      ref={svgRef}
                      viewBox="0 0 1006 806"
                      className="w-[30vw] h-[100vh]"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <motion.line
                        x1="8"
                        y1="8"
                        x2="12"
                        y2="800"
                        stroke="#ffffff"
                        strokeWidth="18"
                        fill="none"
                        initial={{ pathLength: 0 }}
                        animate={controls}
                        transition={{ type: "spring", damping: 10, mass: 0.5, duration: 0.256, delay: 0.2 }}
                      />
                      <motion.line
                        x1="500"
                        y1="302.5"
                        x2="500"
                        y2="1300"
                        transform="translate(500.250000, 800.000000) rotate(-90.000000) translate(-500.250000, -800.000000) "
                        stroke="#ffffff"
                        strokeWidth="14"
                        fill="none"
                        initial={{ pathLength: 0 }}
                        animate={controls}
                        transition={{ type: "spring", damping: 10, mass: 0.5, duration: 0.256, delay: 0.456 }}
                      />
                    </svg>
                  </div>
                </div>
              )}
              {data.Text && (
                <div className="absolute z-[2] top-0 left-0 w-full h-[50vh] md:h-[60vh] lg:h-[80vh]">
                  <div className="flex items-center justify-center text-center h-[50vh] md:h-[60vh] lg:h-[80vh]">
                    <div className="basis-full mx-auto max-w-6xl lg:max-w-7xl 2xl:max-w-8xl px-12 md:px-24 lg:px-32 xl:px-48">
                      {data.Subheadline && (
                        <motion.p
                          variants={springVariants}
                          initial="invisible"
                          whileInView="visible"
                          viewport={springViewport}
                          className={`mb-4 text-text tracking-text leading-text text-center font-semibold max-w-5xl mx-auto drop-shadow-md ${
                            isBlack ? "text-black" : "text-white"
                          }`}
                        >
                          {data.Subheadline}
                        </motion.p>
                      )}
                      <motion.div
                        variants={springVariants}
                        initial="invisible"
                        whileInView="visible"
                        viewport={springViewport}
                        className={`richtextstage drop-shadow-md ${isBlack ? "text-black" : "text-white"}`}
                        dangerouslySetInnerHTML={{
                          __html: data.Text,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-radial-overlay z-[1]"></div>
            </div>
            {isRichTextCaption ? (
              <>
                {data.Media.caption && (
                  <p className="md:absolute inline-flex px-4 sm:px-4 mt-2 mb-0 pr-12 md:pr-24 lg:pr-32 text-text-sm tracking-text-sm leading-text-sm text-black/50 max-w-4xl">
                    {data.Media.caption}
                  </p>
                )}
              </>
            ) : (
              <div className="caption-rich-text-stage" dangerouslySetInnerHTML={{ __html: data.captionText }} />
            )}
          </div>
        )}
      </motion.div>
    </article>
  )};

  export default Stages;
